import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';
import { pageVisibility, type PageId } from '../lib/pageAccess';
import PageHeader from './PageHeader';

/**
 * PAGE guard — « a page the nav hides must not open by URL » (L3, 2026-10-12).
 *
 * ⚠️ THIS IS UI ONLY. Hiding a link has never been a protection, and this guard
 * protects nothing either: the server keeps enforcing the READ
 * (`requireBandAccess('viewer')` on every band-scoped mount, plus the explicit
 * `content.*.view` denies) and the WRITE separately. What this component removes
 * is an UI INCONSISTENCY: the sidebar hid a page the member cannot use, but typing
 * the URL opened it anyway. It changes NOTHING on the mounts or the server guards.
 *
 * SINGLE SOURCE: the verdict is `pageVisibility(...)` — the SAME function the
 * sidebar (`App.tsx`) and the « what does X see? » preview (`memberPreview.ts`)
 * read. NO rule is re-implemented here; the inputs are copied one-for-one from the
 * sidebar's own call, so nav == preview == guard by construction.
 *
 * Before this, only 3 routes had a page guard (`RequireLayoutAccess` on the 3 PDF
 * layout editors); every other page opened by direct URL for a member whose nav
 * hid it (measured: a `viewer` typing `/band/<slug>/config/general`, `/musicians`,
 * `/crew`, `/venues`, `/contacts`, `/availability` got the page).
 *
 * `globalRoute`: the page is NOT mounted under `/band/:bandSlug` — the Tour section
 * mounts its pages under `/tour/…` (Venues, Contacts). Two consequences:
 *   1. there is no slug to wait on, so `bandPending` is skipped;
 *   2. with NO active band, the sidebar still lists those links (`keepAll`, the
 *      documented historical exception), so the guard must render the page instead
 *      of inventing a refusal the nav would not show — nav and guard stay aligned.
 *
 * `bandPending`: a direct URL open of `/band/:slug/…` lets `AppLayout` resolve the
 * slug into the active band in an effect AFTER the first render. Stay neutral while
 * it settles (plus while the profile list is still loading) instead of flashing
 * « no access » at a member who has the right — the same subtlety as
 * `RequireLayoutAccess`.
 */
export interface RequirePageAccessProps {
  /** A page id from `pageAccess.ts` (the same id the sidebar and the preview use). */
  page: PageId;
  children: ReactNode;
  /** Route served OUTSIDE `/band/:bandSlug` (Tour section: `/tour/…`). */
  globalRoute?: boolean;
}

export default function RequirePageAccess({ page, children, globalRoute }: RequirePageAccessProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeProfileId, getProfile, loading } = useProfileContext();
  const { bandSlug } = useParams<{ bandSlug?: string }>();

  // Same expression as the sidebar section in App.tsx (single source of truth).
  const isSuperAdmin = user?.global_role === 'superadmin';
  const isShowcase = !!user?.is_showcase;
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandRole = isSuperAdmin ? 'owner' : activeProfile?.my_role;

  // Stay neutral while the band is being resolved (slug effect + profile load).
  const bandPending = loading
    || (!globalRoute && !!bandSlug
      && (!activeProfile || (activeProfile.slug !== bandSlug && activeProfile.id !== bandSlug)));
  if (bandPending) return <div className="page" />;

  // Mirrors the sidebar's `keepAll` for the Tour section: with no active band the
  // nav still lists its (global) pages, so the guard cannot answer « hidden ».
  if (globalRoute && !activeProfileId) return <>{children}</>;

  const vis = pageVisibility({
    isSuperAdmin,
    isShowcase,
    bandRole,
    permissions: activeProfile?.permissions,
    denied: activeProfile?.denied,
    linked: !!activeProfileId,
  });
  if (vis[page]) return <>{children}</>;

  // Refused — same spirit as `LayoutAccessDenied`, with a way back to the band home.
  const home = activeProfile?.slug ? `/band/${activeProfile.slug}/home` : '/home';
  return (
    <div className="page">
      <PageHeader title={t('pageGuard.noAccessTitle')} />
      <p className="empty-hint">{t('pageGuard.noAccess')}</p>
      <p>
        <Link className="nav-link" to={home}>{t('pageGuard.back')}</Link>
      </p>
    </div>
  );
}
